import * as Sentry from "@sentry/react";
import moment from "moment";
import React from "react";
import DatePicker from "react-datepicker";

import MeetingsPanel from "@apps/meetings/meetings-panel";
import {
  LeftSidePanelState,
  currentOrganizationVar,
  leftSidePanelVar,
} from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

const SidebarDatePickerButton = React.forwardRef<
  HTMLButtonElement,
  { value?: string; onClick?: () => void }
>(({ value, onClick }, ref) => (
  <button
    ref={ref}
    onClick={onClick}
    className="text-gray-500 text-xs flex items-center gap-0.5 rounded px-2 py-1 hover:bg-nav-item-active"
  >
    {value}
  </button>
));

const SidebarMeetings = ({
  sidebarSelectedDay,
  onChangeSidebarSelectedDay,
  menuIsCollapsed,
  leftSidePanel,
}: {
  leftSidePanel: LeftSidePanelState;
  menuIsCollapsed: boolean;
  sidebarSelectedDay: string;
  onChangeSidebarSelectedDay: (newSidebarSelectedDay: string) => void;
}) => {
  const currentOrganization = currentOrganizationVar();
  const meetingsPanelOpen =
    leftSidePanel === "meetings" || typeof leftSidePanel === "object";
  const reportsPanelOpen = leftSidePanel === "reports";
  const settingsPanelOpen = leftSidePanel === "settings";

  // Reset to today when the tab has been hidden for at least 30 minutes.
  // Only applies in compact mode (when the full meetings panel is not open).
  React.useEffect(() => {
    if (meetingsPanelOpen) return;

    const IDLE_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
    let hiddenAt: number | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
      } else if (document.visibilityState === "visible") {
        const wasIdleLongEnough =
          hiddenAt !== null && Date.now() - hiddenAt >= IDLE_THRESHOLD_MS;
        if (wasIdleLongEnough) {
          const today = moment().format("YYYY-MM-DD");
          if (sidebarSelectedDay !== today) {
            onChangeSidebarSelectedDay(today);
          }
        }
        hiddenAt = null;
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sidebarSelectedDay, onChangeSidebarSelectedDay, meetingsPanelOpen]);

  if (menuIsCollapsed) return null;

  return (
    <>
      {/* Meetings section */}
      {!menuIsCollapsed &&
        currentOrganization.featureFlags.meetings &&
        !reportsPanelOpen &&
        !settingsPanelOpen && (
          <div
            className={classNames(
              "bg-sidenav-back flex flex-col border-gray-200",
              meetingsPanelOpen
                ? "flex-1 border-b overflow-hidden"
                : "border-t",
            )}
          >
            <div
              className={classNames(
                "shrink-0 px-3",
                !meetingsPanelOpen && "pt-5",
              )}
            >
              {meetingsPanelOpen ? null : (
                <div className="flex items-center justify-between">
                  <DatePicker
                    selected={moment(sidebarSelectedDay).toDate()}
                    onChange={(date) => {
                      if (date) {
                        const newDay = moment(date).format("YYYY-MM-DD");
                        onChangeSidebarSelectedDay(newDay);
                      }
                    }}
                    dateFormat="EEE, MMM d"
                    popperPlacement="bottom-start"
                    popperClassName="z-tooltip"
                    portalId="portal-react-datepicker"
                    popperProps={{ strategy: "fixed" }}
                    customInput={
                      <SidebarDatePickerButton
                        value={moment(sidebarSelectedDay).format("ddd, MMM D")}
                      />
                    }
                  />
                  <div className="flex items-center gap-0.5">
                    {!moment(sidebarSelectedDay).isSame(moment(), "day") && (
                      <Button
                        tooltip="Jump to Today"
                        icon={getTopicflowIcon("today")}
                        theme={buttonTheme.iconGray}
                        size={ButtonSize.medium}
                        onClick={() =>
                          onChangeSidebarSelectedDay(
                            moment().format("YYYY-MM-DD"),
                          )
                        }
                      />
                    )}
                    <>
                      <Button
                        icon={getTopicflowIcon("chevronLeft")}
                        theme={buttonTheme.iconGray}
                        size={ButtonSize.medium}
                        onClick={() =>
                          onChangeSidebarSelectedDay(
                            moment(sidebarSelectedDay)
                              .subtract(1, "day")
                              .format("YYYY-MM-DD"),
                          )
                        }
                      />
                      <Button
                        icon={getTopicflowIcon("chevronRight")}
                        theme={buttonTheme.iconGray}
                        size={ButtonSize.medium}
                        onClick={() =>
                          onChangeSidebarSelectedDay(
                            moment(sidebarSelectedDay)
                              .add(1, "day")
                              .format("YYYY-MM-DD"),
                          )
                        }
                      />
                    </>
                  </div>
                </div>
              )}
            </div>
            <Sentry.ErrorBoundary
              fallback={<div>Cannot load meetings.</div>}
              showDialog
            >
              <MeetingsPanel
                compact={!meetingsPanelOpen}
                externalSelectedDay={sidebarSelectedDay}
                onChangeSelectedDay={onChangeSidebarSelectedDay}
                leftSidePanel={leftSidePanel}
                onClose={
                  meetingsPanelOpen ? () => leftSidePanelVar(false) : undefined
                }
                onBack={
                  meetingsPanelOpen ? () => leftSidePanelVar(false) : undefined
                }
              />
            </Sentry.ErrorBoundary>
            {!meetingsPanelOpen && (
              <div className="mt-1 px-3">
                <Button
                  theme="blueText"
                  size={ButtonSize.medium}
                  onClick={() => {
                    leftSidePanelVar("meetings");
                  }}
                  className="text-[13px]"
                >
                  View all meetings
                  <TopicflowIcon type="arrowRight" className="h-3 w-3" />
                </Button>
              </div>
            )}
          </div>
        )}
    </>
  );
};

export default React.memo(SidebarMeetings);
